import cx from "classnames";
import moment from "moment";
import React, { memo, useMemo } from "react";

import { WorkbenchDateFormats } from "@common/util/dateFormats";
import {
  DataPointValue,
  GroupTaxonomyEntry,
  NonGroupTaxonomyEntry,
  State,
  TaxonomyEntryType,
} from "../../types";
import { AddButton } from "../AddButton";
import { DuplicateButton } from "../DuplicateButton";
import { RemoveButton } from "../RemoveButton";

export type GroupValues = Array<{
  [dpKey: string]: Array<DataPointValue<any>>;
}>;

type Props = Readonly<{
  collapsed: boolean;
  group: GroupTaxonomyEntry;
  index: number;
  onAddGroup: () => void;
  onClearGroup: () => void;
  onDuplicateGroup: () => void;
  onRemoveGroup: () => void;
  onToggleCollapse: () => void;
  review: boolean;
  taxonomy: State["taxonomy"];
  values: GroupValues;
}>;

const getTitleOfCollapsedValues = (
  group: GroupTaxonomyEntry,
  index: number,
  taxonomy: Props["taxonomy"],
  values: Props["values"],
): string => {
  try {
    for (const child of group.children) {
      const dataPoint = child as NonGroupTaxonomyEntry;
      const dataPointKey = child.id;
      if (!values[index][dataPointKey]) {
        continue;
      }
      switch (dataPoint.type) {
        case TaxonomyEntryType.Date:
        case TaxonomyEntryType.DateTime:
        case TaxonomyEntryType.Time:

          for (const value of values[index][dataPointKey]) {
            if (value.valueRaw.length > 0) {
              return `${dataPoint.question}: ${moment(value.value).format(
                WorkbenchDateFormats[dataPoint.type],
              )}`;
            }
          }
          break;
        case TaxonomyEntryType.Number:
          for (const value of values[index][dataPointKey]) {
            if (value.valueRaw.length > 0) {
              return `${dataPoint.question}: ${value.value.toString(10)}`;
            }
          }
          break;
        case TaxonomyEntryType.Enum:
        case TaxonomyEntryType.Selection:
        case TaxonomyEntryType.String:
          for (const value of values[index][dataPointKey]) {
            if (value.valueRaw.length > 0) {
              return `${dataPoint.question}: ${value.value}`;
            }
          }
          break;
        default:
          break;
      }
    }
  } catch (e) {
    console.log(group + index);
    console.log(e);
  }
  return "(Empty)";
};

const MultipleGroupActionsComponent = ({
  collapsed,
  group,
  index,
  onAddGroup,
  onClearGroup,
  onDuplicateGroup,
  onRemoveGroup,
  onToggleCollapse,
  review,
  taxonomy,
  values,
}: Props) => {
  const title = useMemo(() => getTitleOfCollapsedValues(group, index, taxonomy, values), [
    group,
    index,
    values,
  ]);
  const removeShouldClear = values.length < 2;
  return (
    <div className="d-flex p-1">
      <div className="group-title mr-auto text-truncate visible" title={title}>
        #{index + 1}. {title}
      </div>      
      <div className="mr-2">
        <AddButton disabled={false} onClick={onAddGroup} />
      </div>
      <div className="mr-2">
        <DuplicateButton disabled={false} onClick={onDuplicateGroup} />
      </div>
      <div className="mr-2">
        <RemoveButton
          clear={removeShouldClear}
          onClick={removeShouldClear ? onClearGroup : onRemoveGroup}
          review={review}
        />
      </div>
      <div className="mr-2">
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onToggleCollapse();
          }}
          title="Collapse/Expand"
        >
         <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="12" transform="matrix(1 0 0 -1 0 24)" fill="#E5E5E9"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M8.77082 13.4999L12.0042 10.2666L15.2375 13.4999C15.5625 13.8249 16.0875 13.8249 16.4125 13.4999C16.7375 13.1749 16.7375 12.6499 16.4125 12.3249L12.5875 8.4999C12.2625 8.1749 11.7375 8.1749 11.4125 8.4999L7.58749 12.3249C7.43144 12.4806 7.34375 12.692 7.34375 12.9124C7.34375 13.1328 7.43144 13.3442 7.58749 13.4999C7.91249 13.8166 8.44582 13.8249 8.77082 13.4999V13.4999Z" fill="#616161"/>
        </svg>
        </a>
      </div>
    </div>
  );
};
MultipleGroupActionsComponent.displayName = "MultipleGroupActions";
export const MultipleGroupActions = memo(MultipleGroupActionsComponent);
